import { ChevronDown, Search } from 'lucide-react';
import type { AdminReviewVisibilityFilter } from '@dukanti/shared';
import { useTranslation } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export interface ReviewsProductFilterOption {
  readonly id: string;
  readonly name: string;
}

interface ReviewsFilterBarProps {
  readonly products: readonly ReviewsProductFilterOption[];
  readonly productId: string | null;
  readonly productSearch: string;
  readonly productOptionsLoading: boolean;
  readonly productOptionsCanPreviousPage: boolean;
  readonly productOptionsHasNextPage: boolean;
  readonly visibility: AdminReviewVisibilityFilter;
  readonly onProductChange: (productId: string | null) => void;
  readonly onProductSearchChange: (search: string) => void;
  readonly onProductOptionsPreviousPage: () => void;
  readonly onProductOptionsNextPage: () => void;
  readonly onVisibilityChange: (visibility: AdminReviewVisibilityFilter) => void;
}

export function ReviewsFilterBar({
  products,
  productId,
  productSearch,
  productOptionsLoading,
  productOptionsCanPreviousPage,
  productOptionsHasNextPage,
  visibility,
  onProductChange,
  onProductSearchChange,
  onProductOptionsPreviousPage,
  onProductOptionsNextPage,
  onVisibilityChange,
}: ReviewsFilterBarProps): React.JSX.Element {
  const { t, dir } = useTranslation();

  return (
    <div
      dir={dir}
      className="border-border bg-card shadow-admin-card mb-4 grid gap-3 rounded-lg border p-4 sm:grid-cols-2"
      data-testid="reviews-filter-bar"
    >
      <div className="space-y-1.5 text-start">
        <label
          htmlFor="reviews-product-search"
          className="text-foreground block text-sm font-medium"
        >
          {t('admin.reviews.filters.product')}
        </label>
        <span className="relative block">
          <Search
            className="text-muted-foreground pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            id="reviews-product-search"
            value={productSearch}
            onChange={(event) => onProductSearchChange(event.target.value)}
            placeholder={t('admin.reviews.filters.productSearch')}
            className="ps-10"
            data-testid="reviews-product-search"
          />
        </span>
        <span className="relative block">
          <select
            value={productId ?? ''}
            onChange={(event) => onProductChange(event.target.value || null)}
            aria-label={t('admin.reviews.filters.product')}
            className="border-input bg-input-background text-foreground focus-visible:ring-ring/50 h-10 w-full appearance-none rounded-lg border bg-none px-3 py-2 pe-10 text-start text-sm outline-none [-moz-appearance:none] [-webkit-appearance:none] focus-visible:ring"
            data-testid="reviews-product-filter"
          >
            <option value="">{t('admin.reviews.filters.allProducts')}</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </select>
          <ChevronDown
            className="text-muted-foreground pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2"
            aria-hidden
          />
        </span>
        <span className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!productOptionsCanPreviousPage || productOptionsLoading}
            onClick={onProductOptionsPreviousPage}
          >
            {t('admin.reviews.filters.previousProducts')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!productOptionsHasNextPage || productOptionsLoading}
            onClick={onProductOptionsNextPage}
          >
            {t('admin.reviews.filters.moreProducts')}
          </Button>
        </span>
      </div>
      <label className="space-y-1.5 text-start">
        <span className="text-foreground text-sm font-medium">
          {t('admin.reviews.filters.visibility')}
        </span>
        <span className="relative block">
          <select
            value={visibility}
            onChange={(event) =>
              onVisibilityChange(event.target.value as AdminReviewVisibilityFilter)
            }
            className="border-input bg-input-background text-foreground focus-visible:ring-ring/50 h-10 w-full appearance-none rounded-lg border bg-none px-3 py-2 pe-10 text-start text-sm outline-none [-moz-appearance:none] [-webkit-appearance:none] focus-visible:ring"
            data-testid="reviews-visibility-filter"
          >
            <option value="all">{t('admin.reviews.visibility.all')}</option>
            <option value="visible">{t('admin.reviews.visibility.visible')}</option>
            <option value="hidden">{t('admin.reviews.visibility.hidden')}</option>
          </select>
          <ChevronDown
            className="text-muted-foreground pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2"
            aria-hidden
          />
        </span>
      </label>
    </div>
  );
}
